import type { MessageKey } from '@bookingdei/i18n/message-key';
import type { IconName } from './icon';

// The customer dashboard navigation model — PRD v2.5 §7.3.2 route table, design IA
// (bd-core.js L631-646 PAGE/NAV/TABS) plus Messages and Claims under More (DR-38).
// One named list per piece of chrome; rail.tsx, top-bar.tsx, account-menu.tsx,
// command-palette.tsx, tab-bar.tsx and more-sheet.tsx all read from here.

/** Which counter drives a badge on a nav row. */
export type BadgeKind = 'bookings' | 'reviews' | 'messages';
export type ShellBadges = Readonly<Record<BadgeKind, number>>;

export interface NavItem {
  id: string;
  to: string;
  key: MessageKey;
  icon: IconName;
  badge?: BadgeKind;
}

const home: NavItem = { id: 'home', to: '/', key: 'user.shell.nav.home', icon: 'home' };
const bookings: NavItem = { id: 'bookings', to: '/bookings', key: 'user.shell.nav.bookings', icon: 'bookings', badge: 'bookings' };
const saved: NavItem = { id: 'saved', to: '/saved', key: 'user.shell.nav.saved', icon: 'saved' };
const reviews: NavItem = { id: 'reviews', to: '/reviews', key: 'user.shell.nav.reviews', icon: 'reviews', badge: 'reviews' };
const account: NavItem = { id: 'account', to: '/account', key: 'user.shell.nav.account', icon: 'account' };
const help: NavItem = { id: 'help', to: '/help', key: 'user.shell.nav.help', icon: 'help' };
const messages: NavItem = { id: 'messages', to: '/messages', key: 'user.shell.nav.messages', icon: 'messages', badge: 'messages' };
const claims: NavItem = { id: 'claims', to: '/claims', key: 'user.shell.nav.claims', icon: 'claims' };

/** Desktop rail: three labelled groups (Bookings · Mine · Account). */
export const railGroups: ReadonlyArray<{ id: string; key: MessageKey; items: readonly NavItem[] }> = [
  { id: 'bookings', key: 'user.shell.group.bookings', items: [home, bookings] },
  { id: 'mine', key: 'user.shell.group.mine', items: [saved, reviews] },
  { id: 'account', key: 'user.shell.group.account', items: [account, help] },
];

/** Mobile tab bar: four links + the More button (the fifth slot, rendered by tab-bar.tsx). */
export const tabItems: ReadonlyArray<NavItem & { tabKey: MessageKey }> = [
  { ...home, tabKey: 'user.shell.tab.home' },
  { ...bookings, tabKey: 'user.shell.tab.bookings' },
  { ...saved, tabKey: 'user.shell.tab.saved' },
  { ...reviews, tabKey: 'user.shell.tab.reviews' },
];

/** Mobile More sheet: Account, Help, then the DR-38 keepers Messages and Claims. */
export const moreItems: readonly NavItem[] = [account, help, messages, claims];

/**
 * Desktop account menu: the design's Account · Bookings · Saved · Help, plus Messages
 * and Claims — on desktop the rail has no More, so this menu is where DR-38 keeps them.
 */
export const accountMenuItems: readonly NavItem[] = [account, bookings, saved, help, messages, claims];

/** Every page the command palette can jump to. */
export const paletteItems: readonly NavItem[] = [home, bookings, saved, reviews, account, help, messages, claims];

/** The nav item a pathname belongs to (for the top-bar title and active states). */
export function navItemForPath(pathname: string): NavItem | undefined {
  if (pathname === '/') return home;
  return paletteItems.find((it) => it.to !== '/' && (pathname === it.to || pathname.startsWith(`${it.to}/`)));
}
